import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DataResolved } from '@app/core/resolvers/generic-resolver.resolver';
import { RadioState } from '@app/core/states/radio.state';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Navigate } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { SchedulerCoreState } from '@radioking/scheduler-core';
import { Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';

import { SchedulerAllPlaylistsRequest } from '../../../../../scheduler-core/src/lib/states/scheduler-core.actions';
import { Program } from '../../models/programs.model';
import { ProgramAddPlaylistModalComponent } from '../modals/program-add-playlist-modal/program-add-playlist-modal.component';

@Component({
  selector: 'rk-program-details',
  templateUrl: './program-details.component.html',
  styleUrls: ['./program-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgramDetailsComponent implements OnInit, OnDestroy {
  @Select(SchedulerCoreState.hasPlaylists)
  hasPlaylists$: Observable<boolean>;

  program$: Observable<Program>;
  subscription = new Subscription();

  @Select(RadioState.currentRadioId)
  radioId$: Observable<number>;

  radioId: number | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.program$ = this.route.data.pipe(
      map<any, DataResolved<Program>>(val => val.program),
      switchMap(val => val.data$),
      filter(val => typeof val !== 'undefined'),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.subscription.add(
      this.radioId$
        .pipe(
          filter(id => !!id),
          distinctUntilChanged(),
        )
        .subscribe(id => {
          if (this.radioId && id !== this.radioId) {
            this.router.navigateByUrl(`/radio/${id}/scheduler/programs`);
          } else {
            this.radioId = id;
            this.getPlaylists(id);
          }
        }),
    );
  }

  @Dispatch()
  getPlaylists(id: number) {
    return new SchedulerAllPlaylistsRequest(id);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  addPlaylist() {
    this.subscription.add(
      this.program$.pipe(take(1)).subscribe(program => {
        this.dialog.open(ProgramAddPlaylistModalComponent, { data: { program } });
      }),
    );
  }

  @Dispatch()
  navigateToLibrary() {
    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);

    return new Navigate(['radio', radioId, 'media']);
  }
}
